/* eslint-disable max-lines-per-function */
import { useCallback, useMemo } from 'react';

import type { PerimeterXEnforcerChallengeWithBlockedUrl } from '@change-corgi/core/perimeterx';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { usePetitionDetailsPageData } from '#src/app/pages/petitionDetails/pageContext';
import type { CallbackData, NormalizedFormValues } from '#src/app/pages/petitionDetails/shared/types';
import { getSpikeTier } from '#src/app/pages/petitionDetails/shared/utils';
import type { ConstituentIdentification } from '#src/app/shared/constituents';
import { useLoginState } from '#src/app/shared/hooks/session';
import { useGenerateSuccessRedirectUrl, useSignPetition as useSignPetitionBase } from '#src/app/shared/hooks/signature';

import { useSignatureTracking } from '../../tracking/useSignatureTracking';
import { useGenerateGuestRedirectUrl } from '../useGenerateGuestRedirectUrl';
import { useSetLocalStoredPendingSignature } from '../useSetLocalStoredPendingSignature';
import { useSetPublicSignatureCookie } from '../useSetPublicSignatureCookie';
import { useSetSessionStoredConstituentStatus } from '../useSetSessionStoredConstituentStatus';
import { useSetSessionStoredReasonForSigning } from '../useSetSessionStoredReasonForSigning';
import { useSignData } from '../useSignData';
import { useSignPetitionAsGuest } from '../useSignPetitionAsGuest';

export type SignPetitionHookResult =
	| { status: 'success'; redirectUrl: string }
	| { status: 'error'; reason: 'other'; error: unknown }
	| { status: 'error'; reason: 'badSession' | 'captcha' | 'invalidEmail' | 'loginStateMismatch' }
	| { status: 'error'; reason: 'perimeterx'; pxResponse: PerimeterXEnforcerChallengeWithBlockedUrl };

type SignFunction = (
	formData: NormalizedFormValues,
	constituentIdentification: ConstituentIdentification | null,
) => Promise<SignPetitionHookResult>;

type StorePendingSignature = (
	pendingSignature: CallbackData,
	options: { guest: 'new' | 'existing' },
) => SignPetitionHookResult;

type CompleteSignature = (
	formData: NormalizedFormValues,
	constituentIdentification: ConstituentIdentification | null,
	redirectUrl: string,
) => SignPetitionHookResult;

/**
 * What the sign form stores and redirects to once a sign ends, shared by both mutations. Each setter is created once
 * here, as each one subscribes to storage or cookie preferences.
 */
function useSignOutcomes(): Readonly<{
	storePendingSignature: StorePendingSignature;
	completeSignature: CompleteSignature;
}> {
	const setLocalStoredPendingSignature = useSetLocalStoredPendingSignature();
	const setPublicSignatureCookie = useSetPublicSignatureCookie();
	const setSessionStoredReasonForSigning = useSetSessionStoredReasonForSigning();
	const setSessionStoredConstituentStatus = useSetSessionStoredConstituentStatus();
	const generateGuestPsfUrl = useGenerateGuestRedirectUrl();
	const generateSuccessRedirectUrl = useGenerateSuccessRedirectUrl({ conserveRecruiter: true });

	// the PSF confirms the pending signature, or shows a banner with a "log in" action based on the user in it
	const storePendingSignature = useCallback<StorePendingSignature>(
		(pendingSignature, { guest }) => {
			setLocalStoredPendingSignature(pendingSignature);
			setPublicSignatureCookie(pendingSignature.formData.public);
			return {
				status: 'success',
				redirectUrl: generateGuestPsfUrl(pendingSignature.formData, { guest }),
			};
		},
		[setLocalStoredPendingSignature, setPublicSignatureCookie, generateGuestPsfUrl],
	);

	const completeSignature = useCallback<CompleteSignature>(
		(formData, constituentIdentification, redirectUrl) => {
			setSessionStoredReasonForSigning(formData);
			setLocalStoredPendingSignature(null);
			setPublicSignatureCookie(formData.public);
			// stored only once the sign succeeds, so pending and failed signs are not enrolled in the
			// constituent experiment
			setSessionStoredConstituentStatus(constituentIdentification);

			return {
				status: 'success',
				redirectUrl: generateSuccessRedirectUrl(redirectUrl),
			};
		},
		[
			setSessionStoredReasonForSigning,
			setLocalStoredPendingSignature,
			setPublicSignatureCookie,
			setSessionStoredConstituentStatus,
			generateSuccessRedirectUrl,
		],
	);

	return useMemo(() => ({ storePendingSignature, completeSignature }), [storePendingSignature, completeSignature]);
}

export function useSignPetition(): SignFunction {
	const { data } = usePetitionDetailsPageData();
	const petitionId = data.petition.id;
	const { signPetitionAsGuestMutationEnabled } = data.featureConfigs;

	const { errorReporter } = useUtilityContext();
	const loginState = useLoginState();

	const signData = useSignData();

	const spikeTier = getSpikeTier(data.petition.momentum);
	const trackingData = useMemo(() => ({ spike_tier: spikeTier }), [spikeTier]);

	const signPetition = useSignPetitionBase(petitionId, { bandits: true, pageContext: 'petitions_show', trackingData });
	const signPetitionAsGuest = useSignPetitionAsGuest({ signData, trackingData });

	const { storePendingSignature, completeSignature } = useSignOutcomes();

	const { trackDoubleOptIn, trackExistingEmail, trackPerimeterX } = useSignatureTracking();

	const signAsGuest: SignFunction = useCallback(
		async (formData, constituentIdentification) => {
			const signResult = await signPetitionAsGuest(formData);

			if (signResult.status === 'success') {
				return completeSignature(formData, constituentIdentification, signResult.redirectUrl);
			}

			switch (signResult.reason) {
				case 'grayListed':
					trackDoubleOptIn();
					return storePendingSignature({ formData }, { guest: 'new' });
				// the existing-email tracking and the returning-guest resubmit are done by the middle-end
				case 'pendingConfirmation':
					return storePendingSignature(
						{ formData, user: { connectedToFacebook: false, passwordSet: false } },
						{ guest: 'existing' },
					);
				// not returned by the middle-end yet: every existing account is a pendingConfirmation (the
				// fix-existing-account-guest-sign plan decides what replaces it)
				case 'existingAccount':
					return storePendingSignature(
						{
							formData,
							user: {
								connectedToFacebook: signResult.loginMethod === 'FACEBOOK',
								passwordSet: signResult.loginMethod === 'PASSWORD',
							},
						},
						{ guest: 'existing' },
					);
				case 'perimeterx':
					trackPerimeterX();
					return signResult;
				case 'badSession':
				case 'captcha':
				case 'invalidEmail':
				case 'loginStateMismatch':
					return signResult;
				default: {
					// a result added without a case above reaches here, where `never` stops the build
					const unhandled: never = signResult;
					return unhandled;
				}
			}
		},
		[signPetitionAsGuest, completeSignature, storePendingSignature, trackDoubleOptIn, trackPerimeterX],
	);

	const signWithSignPetition: SignFunction = useCallback(
		async (formData, constituentIdentification) => {
			const signResult = await signPetition({ signature: { ...formData, ...signData } });

			if (signResult.status === 'error') {
				switch (signResult.reason) {
					case 'grayListed':
						trackDoubleOptIn();
						return storePendingSignature({ formData }, { guest: 'new' });
					case 'guestSignByExistingUser': {
						const { user } = signResult;
						const result = storePendingSignature({ formData, user }, { guest: 'existing' });
						trackExistingEmail(user);
						// eslint-disable-next-line max-depth
						if (signResult.pendingAvailable) {
							// this will result in sending an email to the user with a confirmation link
							await signPetition({
								signature: {
									...formData,
									...signData,
									resubmit: true,
									pendingReason: 'RETURNING_GUEST_USER',
								},
							});
						}
						return result;
					}
					case 'perimeterx':
						trackPerimeterX();
						return { status: 'error', reason: 'perimeterx', pxResponse: signResult.pxResponse };
					case 'badSession':
					case 'captcha':
					case 'invalidEmail':
						return signResult;
					default:
				}
			}

			return completeSignature(formData, constituentIdentification, signResult.redirectUrl);
		},
		[
			signPetition,
			signData,
			completeSignature,
			storePendingSignature,
			trackExistingEmail,
			trackDoubleOptIn,
			trackPerimeterX,
		],
	);

	// any sign form shown to a GUEST viewer: the guest form and the two-step form (UnauthenticatedSignForm) alike
	const signsAsGuest = signPetitionAsGuestMutationEnabled && loginState === 'GUEST';

	return useCallback(
		async (formData, constituentIdentification) => {
			try {
				return await (signsAsGuest ? signAsGuest : signWithSignPetition)(formData, constituentIdentification);
			} catch (error) {
				// only unexpected errors reach this catch — known outcomes (badSession, captcha,
				// invalidEmail, perimeterx, ...) are returned, not thrown, by signPetition and signPetitionAsGuest.
				// Thrown on the guest path: a form without an email, and an unexpected failure of the returning-guest
				// resubmit. Report them to Sentry with distinct sign-form context before surfacing as a submit
				// error (CHANGE-86207)
				void errorReporter.report({
					error: error instanceof Error ? error : new Error('Sign form submission failed', { cause: error }),
					context: { feature: 'sign_form' },
					params: { petitionId },
				});
				return { status: 'error', reason: 'other', error };
			}
		},
		[errorReporter, petitionId, signsAsGuest, signAsGuest, signWithSignPetition],
	);
}
